import React, { useCallback, useMemo, useState } from 'react';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { useParams } from 'react-router';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { DatabaseSchedule } from 'libs/end-to-end/src/types/databases/scheduling/database-schedule';
import { DatabaseScheduleConfigurationType } from 'libs/end-to-end/src/types/databases/scheduling/database-schedule';
import { DatabaseScheduleEditableFields } from 'libs/end-to-end/src/types/databases/scheduling/database-schedule';
import { DatabaseScheduleConfigurationTypes } from 'libs/end-to-end/src/types/databases/scheduling/database-schedule';
import { DatabaseScheduleForRead } from 'libs/end-to-end/src/types/databases/scheduling/database-schedule';
import { DatabaseScheduleStatus } from 'libs/end-to-end/src/types/databases/scheduling/database-schedule';
import { DatabaseScheduleStatuses } from 'libs/end-to-end/src/types/databases/scheduling/database-schedule';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { GetDatabaseScheduleRunsOutput } from 'libs/end-to-end/src/types/databases/scheduling/database-schedule-run';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { getDateTimeAsReadableString } from 'libs/end-to-end/src/dates';
import { useSimpleReadWritePattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadWritePattern';
import { useSimpleWritePattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleWritePattern';
import { UnknownObject } from 'libs/end-to-end/src/generic';
import { ConfigureHiringReadinessScoreWriteBackDetails } from 'apps/sigma2-ui/src/components/workflows/ConfigureHiringReadinessScoreWriteBackDetails';
import { ConfigureHiringReadinessExplanationWriteBackDetails } from 'apps/sigma2-ui/src/components/workflows/ConfigureHiringReadinessExplanationWriteBackDetails';
import { ConfigureHiringNotificationsDetails } from 'apps/sigma2-ui/src/components/workflows/ConfigureHiringNotificationsDetails';
import { DataSets } from 'libs/end-to-end/src/types/databases/data-sets/data-sets';
import { ConfigurePromotionReadinessScoreWriteBackDetails } from 'apps/sigma2-ui/src/components/workflows/ConfigurePromotionReadinessScoreWriteBackDetails';
import { ConfigurePromotionReadinessExplanationWriteBackDetails } from 'apps/sigma2-ui/src/components/workflows/ConfigurePromotionReadinessExplanationWriteBackDetails';
import { S2Checkbox } from 'apps/sigma2-ui/src/components/ui/Checkbox';
import { DatabaseSchedulePullOrUploadDataSetOverrideConfig } from 'apps/sigma2-ui/src/data-wizard/components/DatabaseSchedulePullOrUploadDataSetOverrideConfig';
import { ConfigureCandidateResumeScoreWriteBackDetails } from 'apps/sigma2-ui/src/components/workflows/ConfigureCandidateResumeScoreWriteBackDetails';
import { ConfigureBadLanguageDetectorWriteBackDetails } from 'apps/sigma2-ui/src/components/workflows/ConfigureBadLanguageDetectorWriteBackDetails';
import { ConfigureAIInterviewerWriteBackDetails } from 'apps/sigma2-ui/src/components/workflows/ConfigureAIInterviewerWriteBackDetails';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';
import { useListOrganizationSecretsPattern } from 'apps/sigma2-ui/src/api/list-organization-secrets-pattern';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { ConfigureApplicantStageChangeUpdateDetails } from 'apps/sigma2-ui/src/components/workflows/ConfigureApplicantStageChangeUpdateDetails';
import { DatabaseScheduleTargetSelector } from 'apps/sigma2-ui/src/data-wizard/components/DatabaseScheduleTargetSelector';
import { DatabaseScheduleRetryableConfigEditor } from 'apps/sigma2-ui/src/data-wizard/components/DatabaseScheduleRetryableConfigEditor';
import { SubTabsTabDefinition } from 'apps/sigma2-ui/src/lib/hooks/useSubTabGroup';
import { useSubTabs } from 'apps/sigma2-ui/src/lib/hooks/useSubTabGroup';
import { SubTabs } from 'apps/sigma2-ui/src/components/ui/SubTabs';
import { TextActiveEditComponent } from 'apps/sigma2-ui/src/components/TextActiveEditComponent';
import { DatabaseRunMetricsPage } from 'apps/sigma2-ui/src/data-wizard/components/DatabaseRunMetricsPage';
import { DatabaseRevisionSchemaOptions } from 'libs/end-to-end/src/types/databases/revisions/database-revision-schema-options';
import { useMyOrganizationsDatabasesAndRevisions } from 'apps/sigma2-ui/src/lib/hooks/useMyOrganizationsDatabasesAndRevisions';
import { MyOrganizationsDatabasesAndRevisions } from 'libs/end-to-end/src/types/me/my-organizations-databases-and-revisions';

/**
 * Returns the latest visible revision id for a database from the me/organizations-databases-and-revisions
 * payload (same as listVisibleRevisionsOrderedBySequence's first revision).
 */
function getLatestVisibleRevisionIdForDatabase(
  myOrganizationsDatabasesAndRevisions:
    | MyOrganizationsDatabasesAndRevisions
    | null
    | undefined,
  organizationId: string,
  databaseId: string,
): string | null {
  return (
    myOrganizationsDatabasesAndRevisions?.organizations?.[organizationId]
      ?.databases?.[databaseId]?.revisions?.latest?.id ?? null
  );
}

export const DatabaseSchedulePage = () => {
  const language = useLanguage();

  const { organizationId, databaseId, scheduleId } = useParams();

  if (!organizationId || !databaseId || !scheduleId) {
    throw new Error('Organization ID and Database ID are required');
  }

  const listSecretsPattern = useListOrganizationSecretsPattern({
    organizationId,
  });

  const patternListDataSetsGetUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/data-sets`;
  }, [organizationId, databaseId]);

  const listDataSetsPattern = useSimpleReadPattern({
    responseSchema: DataSets,
    canRead: true,
    getUrlFn: patternListDataSetsGetUrlFn,
  });

  const readWriteScheduleConfigPattern = useSimpleReadWritePattern(
    useMemo(() => {
      return {
        getReadUrlFn: () =>
          `${getApiBaseUrl()}/v2/organizations/${organizationId}/databases/${databaseId}/schedule/${scheduleId}`,
        getWriteUrlFn: () =>
          `${getApiBaseUrl()}/v2/organizations/${organizationId}/databases/${databaseId}/schedule/${scheduleId}`,
        canRead: true,
        canWrite: true,
        writeMethod: 'PUT',
        writeSchema: DatabaseScheduleEditableFields,
        responseSchema: DatabaseScheduleForRead,
      };
    }, [databaseId, organizationId, scheduleId]),
  );

  const readRunsPattern = useSimpleReadPattern(
    useMemo(() => {
      return {
        getUrlFn: () =>
          `${getApiBaseUrl()}/v2/organizations/${organizationId}/databases/${databaseId}/schedule/${scheduleId}/runs`,
        canRead: readWriteScheduleConfigPattern.status === 'done',
        responseSchema: GetDatabaseScheduleRunsOutput,
      };
    }, [
      databaseId,
      organizationId,
      readWriteScheduleConfigPattern.status,
      scheduleId,
    ]),
  );

  const showedRuns = useMemo(() => {
    if (readRunsPattern.status === 'done') {
      return readRunsPattern.entity.sort((a, b) => {
        return b.startedAtEpoch - a.startedAtEpoch;
      });
    }
    return [];
  }, [readRunsPattern]);

  const latestRunId = useMemo(() => {
    return showedRuns.length > 0 ? showedRuns.at(0)?.runId : null;
  }, [showedRuns]);

  const syncDataWritePattern = useSimpleWritePattern(
    useMemo(() => {
      return {
        getUrlFn: () =>
          `${getApiBaseUrl()}/v2/organizations/${organizationId}/databases/${databaseId}/schedule/${scheduleId}/sync`,
        canWrite: true,
        writeSchema: UnknownObject,
        responseSchema: UnknownObject,
      };
    }, [databaseId, organizationId, scheduleId]),
  );

  const { myOrganizationsDatabasesAndRevisions } =
    useMyOrganizationsDatabasesAndRevisions({
      needsFullRevisions: true,
    });

  const revisionId = useMemo(
    () =>
      getLatestVisibleRevisionIdForDatabase(
        myOrganizationsDatabasesAndRevisions,
        organizationId,
        databaseId,
      ),
    [myOrganizationsDatabasesAndRevisions, organizationId, databaseId],
  );

  const schemaOptionsReadPattern = useSimpleReadPattern(
    useMemo(() => {
      return {
        getUrlFn: () =>
          `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/revisions/${revisionId}/schema-options`,
        canRead: !!revisionId,
        responseSchema: DatabaseRevisionSchemaOptions,
      };
    }, [databaseId, organizationId, revisionId]),
  );

  const uniqueValuesByColumnForSchedule = useMemo(() => {
    if (schemaOptionsReadPattern.status !== 'done') {
      return [];
    }
    return schemaOptionsReadPattern.entity.uniqueValuesByColumn ?? [];
  }, [schemaOptionsReadPattern]);

  const [internalCurrentEditedSchedule, setCurrentEditedSchedule] =
    useState<DatabaseSchedule | null>(null);

  const currentEditedSchedule = useMemo(() => {
    if (internalCurrentEditedSchedule !== null) {
      return internalCurrentEditedSchedule;
    }
    if (readWriteScheduleConfigPattern.status === 'done') {
      return readWriteScheduleConfigPattern.entity.schedule ?? null;
    }
    return null;
  }, [internalCurrentEditedSchedule, readWriteScheduleConfigPattern]);

  const setPartialEditedSchedule = useCallback(
    (partialSchedule: Partial<DatabaseSchedule>) => {
      if (currentEditedSchedule) {
        setCurrentEditedSchedule({
          ...currentEditedSchedule,
          ...partialSchedule,
        });
      }
    },
    [currentEditedSchedule],
  );

  const [currentSelectedRunId, setCurrentSelectedRunId] = useState<
    string | null
  >(null);

  const reloadRuns = useCallback(() => {
    if (readRunsPattern.status === 'done') {
      readRunsPattern.reloadEntity();
    }
  }, [readRunsPattern]);

  const runSyncData = useCallback(() => {
    if (syncDataWritePattern.status === 'done') {
      syncDataWritePattern.saveEntity({}, () => {
        reloadRuns();
      });
    }
  }, [reloadRuns, syncDataWritePattern]);

  const basicConfigSubTabDefinition = useCallback(
    ({
      schedule,
      setSchedule,
    }: {
      schedule: DatabaseSchedule;
      setSchedule: (schedule: Partial<DatabaseSchedule>) => void;
    }) => {
      const shouldDisableProducePullIterations = Boolean(
        schedule.shouldDisableProducePullIterations,
      );
      const body = (
        <Spacing size={'md'} direction={'vertical'}>
          <Spacing size={'sm'} direction={'vertical'}>
            <S2Checkbox
              checked={shouldDisableProducePullIterations}
              onChange={() => {
                setSchedule({
                  shouldDisableProducePullIterations:
                    !shouldDisableProducePullIterations,
                });
              }}
            >
              Disable pulling new dataset versions in runs
            </S2Checkbox>

            <S2Checkbox
              checked={schedule.enableDagBasedDataSync}
              onChange={() => {
                setSchedule({
                  enableDagBasedDataSync: !schedule.enableDagBasedDataSync,
                });
              }}
            >
              Enable DAG based Data Sync
            </S2Checkbox>

            <S2Checkbox
              checked={schedule.skipTopLevelVerification ?? false}
              onChange={() => {
                setSchedule({
                  skipTopLevelVerification: !schedule.skipTopLevelVerification,
                });
              }}
            >
              Skip Top Level Verification (proceed even if verification fails)
            </S2Checkbox>

            <S2Checkbox
              checked={schedule.skipCandidateRevisionIndex ?? false}
              onChange={() => {
                setSchedule({
                  skipCandidateRevisionIndex:
                    !schedule.skipCandidateRevisionIndex,
                });
              }}
            >
              Skip Candidate Revision Index (do not run candidate index builder)
            </S2Checkbox>

            <SelectBoxWithSearch
              type={'single'}
              setSelected={(newValue) => {
                setSchedule({
                  forceRunPromotionCopilot: newValue,
                });
              }}
              selected={schedule.forceRunPromotionCopilot ?? 'none'}
              list={[
                {
                  optionValue: 'none',
                  optionLabel: 'None',
                },
                {
                  optionValue: 'scores',
                  optionLabel: 'Scores',
                },
                {
                  optionValue: 'scores-and-explanations',
                  optionLabel: 'Scores and Explanations',
                },
              ]}
              label={
                'Force Promotion Co-pilot Mode (regardless of write-back configs)'
              }
            />

            <SelectBoxWithSearch
              type={'single'}
              setSelected={(newValue) => {
                setSchedule({
                  forceRunHiringCopilot:
                    newValue === 'none' ? undefined : newValue,
                });
              }}
              selected={schedule.forceRunHiringCopilot ?? 'none'}
              list={[
                {
                  optionValue: 'none',
                  optionLabel: 'None',
                },
                {
                  optionValue: 'scores',
                  optionLabel: 'Scores',
                },
                {
                  optionValue: 'scores-and-explanations',
                  optionLabel: 'Scores and Explanations',
                },
              ]}
              label={
                'Force Hiring Co-pilot Mode (regardless of write-back configs)'
              }
            />
          </Spacing>

          <DatabaseScheduleTargetSelector
            currentTarget={schedule.target}
            setCurrentTarget={(newValue) => {
              setSchedule({
                target: newValue,
              });
            }}
            dataSets={
              listDataSetsPattern.status === 'done'
                ? listDataSetsPattern.entity.dataSets
                : []
            }
          />

          <DatabaseSchedulePullOrUploadDataSetOverrideConfig
            organizationId={organizationId}
            databaseId={databaseId}
            configs={schedule.pullOrUpdateOverrideDataSetConfigs}
            setConfigs={(newValue) => {
              if (!Array.isArray(newValue)) {
                return;
              }
              setSchedule({
                pullOrUpdateOverrideDataSetConfigs: newValue,
              });
            }}
          />

          <DatabaseScheduleRetryableConfigEditor
            retryableConfig={schedule.retryableConfig ?? { type: 'disabled' }}
            setRetryableConfig={(retryableConfig) => {
              setSchedule({ retryableConfig });
            }}
          />
        </Spacing>
      );

      const header = () => <div>Basic Config</div>;
      return {
        header,
        body: () => body,
      };
    },
    [organizationId, databaseId, listDataSetsPattern],
  );

  const runsSubTabDefinition = useCallback(() => {
    const body = (
      <Spacing size={'xl'} direction={'vertical'}>
        {currentSelectedRunId && scheduleId && (
          <>
            <Button
              behavior={{
                type: 'onClick',
                onClick: () => {
                  setCurrentSelectedRunId(null);
                },
              }}
              size="small"
              variant={'primary'}
            >
              <S2Typography
                variant={'headline-5-alt'}
                translatedContent={'Back'}
                textColorVariant={'white'}
              />
            </Button>
            <DatabaseRunMetricsPage
              runId={currentSelectedRunId}
              organizationId={organizationId}
              databaseId={databaseId}
              scheduleId={scheduleId}
            />
          </>
        )}
        {!currentSelectedRunId && (
          <>
            {' '}
            <S2Typography
              variant={'headline-5-alt'}
              translatedContent={language.runs()}
            />
            <Button
              behavior={{
                type: 'onClick',
                onClick: () => {
                  runSyncData();
                },
              }}
              variant={'primary'}
            >
              <S2Typography
                variant={'headline-5-alt'}
                translatedContent={'Run Manual Sync'}
                textColorVariant={'white'}
              />
            </Button>
            <Table
              loading={<></>}
              isLoading={readRunsPattern.status === 'loading'}
              columnDefinitions={[
                {
                  id: 'runId',
                  header: () => <div>Run Id</div>,
                  cell: (row) => {
                    if (row.item.sfnExecutionArnUrl) {
                      return (
                        <a
                          href={row.item.sfnExecutionArnUrl}
                          target={'_blank'}
                          rel={'noreferrer'}
                          className={'text-blue-600 underline'}
                        >
                          {row.item.runId}
                        </a>
                      );
                    } else {
                      return <div>{row.item.runId}</div>;
                    }
                  },
                },
                {
                  id: 'startedAt',
                  header: () => <div>Started At</div>,
                  cell: (row) => (
                    <div>
                      {getDateTimeAsReadableString(
                        new Date(row.item.startedAtEpoch),
                      )}
                    </div>
                  ),
                },
                {
                  id: 'endedAt',
                  header: () => <div>Ended At</div>,
                  cell: (row) => (
                    <div>
                      {row.item.endedAtEpoch
                        ? getDateTimeAsReadableString(
                            new Date(row.item.endedAtEpoch),
                          )
                        : 'N/A'}
                    </div>
                  ),
                },
                {
                  id: 'status',
                  header: () => <div>Status</div>,
                  cell: (row) => (
                    <div>
                      {row.item.status}
                      {row.item.errorReason &&
                        row.item.errorReason.trim() !== '' && (
                          <div
                            className="text-red-500 truncate"
                            title={row.item.errorReason}
                          >
                            {row.item.errorReason}
                          </div>
                        )}
                    </div>
                  ),
                },
                {
                  id: 'view',
                  header: () => <div>Metrics</div>,
                  cell: (row) => {
                    return (
                      <Button
                        behavior={{
                          type: 'onClick',
                          onClick: () => {
                            setCurrentSelectedRunId(row.item.runId);
                          },
                        }}
                        size="small"
                        variant={'primary'}
                      >
                        <S2Typography
                          variant={'headline-5-alt'}
                          translatedContent={'View'}
                          textColorVariant={'white'}
                        />
                      </Button>
                    );
                  },
                },
              ]}
              items={showedRuns}
            />
          </>
        )}
      </Spacing>
    );

    const header = () => <div>Runs</div>;
    return {
      header,
      body: () => body,
    };
  }, [
    currentSelectedRunId,
    scheduleId,
    organizationId,
    databaseId,
    language,
    readRunsPattern,
    showedRuns,
    runSyncData,
  ]);

  const writeBackConfigSubTabDefinition = useCallback(
    ({
      schedule,
      setSchedule,
    }: {
      schedule: DatabaseSchedule;
      setSchedule: (schedule: Partial<DatabaseSchedule>) => void;
    }) => {
      const body = (
        <Spacing size={'md'} direction={'vertical'}>
          <ConfigureHiringReadinessScoreWriteBackDetails
            details={schedule.hiringScoreWriteBackConfig ?? null}
            updateDetails={(newValue) => {
              setSchedule({
                hiringScoreWriteBackConfig: newValue ?? undefined,
              });
            }}
            listSecretsPattern={listSecretsPattern}
            organizationId={organizationId}
            listDataSetsPattern={listDataSetsPattern}
            uniqueValuesByColumn={uniqueValuesByColumnForSchedule}
          />

          <ConfigureHiringReadinessExplanationWriteBackDetails
            details={schedule.hiringExplanationWriteBackConfig ?? null}
            updateDetails={(newValue) => {
              setSchedule({
                hiringExplanationWriteBackConfig: newValue ?? undefined,
              });
            }}
            listSecretsPattern={listSecretsPattern}
            organizationId={organizationId}
            listDataSetsPattern={listDataSetsPattern}
          />

          <ConfigureCandidateResumeScoreWriteBackDetails
            details={schedule.candidateResumeScoreWriteBackConfig ?? null}
            updateDetails={(newValue) => {
              setSchedule({
                candidateResumeScoreWriteBackConfig: newValue ?? undefined,
              });
            }}
            listSecretsPattern={listSecretsPattern}
            organizationId={organizationId}
            listDataSetsPattern={listDataSetsPattern}
          />

          <ConfigureBadLanguageDetectorWriteBackDetails
            details={schedule.badLanguageDetectorWriteBackConfig ?? null}
            updateDetails={(newValue) => {
              setSchedule({
                badLanguageDetectorWriteBackConfig: newValue ?? undefined,
              });
            }}
            listSecretsPattern={listSecretsPattern}
            organizationId={organizationId}
            listDataSetsPattern={listDataSetsPattern}
          />

          <ConfigureApplicantStageChangeUpdateDetails
            details={schedule.applicantStageChangeUpdateConfig ?? null}
            updateDetails={(newValue) => {
              setSchedule({
                applicantStageChangeUpdateConfig: newValue ?? undefined,
              });
            }}
            listSecretsPattern={listSecretsPattern}
            organizationId={organizationId}
            listDataSetsPattern={listDataSetsPattern}
          />

          <ConfigurePromotionReadinessScoreWriteBackDetails
            details={schedule.promotionScoreWriteBackConfig ?? null}
            updateDetails={(newValue) => {
              setSchedule({
                promotionScoreWriteBackConfig: newValue ?? undefined,
              });
            }}
            listSecretsPattern={listSecretsPattern}
            organizationId={organizationId}
            listDataSetsPattern={listDataSetsPattern}
          />

          <ConfigurePromotionReadinessExplanationWriteBackDetails
            details={schedule.promotionExplanationWriteBackConfig ?? null}
            updateDetails={(newValue) => {
              setSchedule({
                promotionExplanationWriteBackConfig: newValue ?? undefined,
              });
            }}
            listSecretsPattern={listSecretsPattern}
            organizationId={organizationId}
            listDataSetsPattern={listDataSetsPattern}
          />

          <ConfigureAIInterviewerWriteBackDetails
            details={schedule.aiInterviewerWriteBackConfig ?? null}
            updateDetails={(newValue) => {
              setSchedule({
                aiInterviewerWriteBackConfig: newValue ?? undefined,
              });
            }}
            listSecretsPattern={listSecretsPattern}
            organizationId={organizationId}
            listDataSetsPattern={listDataSetsPattern}
          />
          <ConfigureHiringNotificationsDetails
            details={schedule.hiringNotificationsConfig ?? null}
            updateDetails={(newValue) => {
              setSchedule({
                hiringNotificationsConfig: newValue ?? undefined,
              });
            }}
            listSecretsPattern={listSecretsPattern}
            organizationId={organizationId}
            listDataSetsPattern={listDataSetsPattern}
          />
        </Spacing>
      );

      const header = () => <div>Write Back Config</div>;
      return {
        header,
        body: () => body,
      };
    },
    [
      organizationId,
      listDataSetsPattern,
      listSecretsPattern,
      uniqueValuesByColumnForSchedule,
    ],
  );

  const metricsSubTabDefinition = useCallback(() => {
    const last7DaysRuns = showedRuns.filter((run) => {
      return run.startedAtEpoch > Date.now() - 7 * 24 * 60 * 60 * 1000;
    });

    const failureRate =
      last7DaysRuns.filter((run) => {
        return run.status === 'Failed';
      }).length / last7DaysRuns.length;

    let totalExecutionTime = 0;
    let totalRunsWithTimes = 0;
    for (const run of last7DaysRuns) {
      if (run.endedAtEpoch && run.startedAtEpoch) {
        totalExecutionTime += run.endedAtEpoch - run.startedAtEpoch;
        totalRunsWithTimes++;
      }
    }
    const averageExecutionTime = totalExecutionTime / totalRunsWithTimes / 1000;
    const totalRuns = last7DaysRuns.length;

    const body = (
      <Spacing size={'md'} direction={'vertical'}>
        <S2Typography
          variant={'headline-4-alt'}
          translatedContent={'Last 7 Days'}
        />
        <Spacing
          size={'md'}
          direction={'horizontal'}
          className="grid grid-cols-3"
        >
          <Spacing
            size="xs"
            direction="vertical"
            className="items-center justify-self-center"
          >
            <S2Typography
              variant={'headline-5-alt'}
              translatedContent={'Average Execution Time'}
            />
            <S2Typography
              variant={'headline-7-alt'}
              translatedContent={`${averageExecutionTime.toFixed(2)}s`}
            />
          </Spacing>
          <Spacing
            size="xs"
            direction="vertical"
            className="items-center justify-self-center"
          >
            <S2Typography
              variant={'headline-5-alt'}
              translatedContent={'Total Runs'}
            />
            <S2Typography
              variant={'headline-7-alt'}
              translatedContent={totalRuns.toString()}
            />
          </Spacing>
          <Spacing
            size="xs"
            direction="vertical"
            className="items-center justify-self-center"
          >
            <S2Typography
              variant={'headline-5-alt'}
              translatedContent={'Failure Rate'}
            />
            <S2Typography
              variant={'headline-7-alt'}
              translatedContent={`${(failureRate * 100).toFixed(2)}%`}
            />
          </Spacing>
        </Spacing>
        {latestRunId && scheduleId && (
          <Spacing size={'md'} direction={'vertical'}>
            <S2Typography
              variant={'headline-4-alt'}
              translatedContent={'Latest Run Metrics'}
            />
            <DatabaseRunMetricsPage
              runId={latestRunId}
              organizationId={organizationId}
              databaseId={databaseId}
              scheduleId={scheduleId}
            />
          </Spacing>
        )}
      </Spacing>
    );

    const header = () => <div>Metrics</div>;
    return {
      header,
      body: () => body,
    };
  }, [showedRuns, latestRunId, scheduleId, organizationId, databaseId]);

  const subTabDefinitions: SubTabsTabDefinition[] = useMemo(() => {
    if (!currentEditedSchedule) {
      return [];
    }
    return [
      basicConfigSubTabDefinition({
        schedule: currentEditedSchedule,
        setSchedule: setPartialEditedSchedule,
      }),
      writeBackConfigSubTabDefinition({
        schedule: currentEditedSchedule,
        setSchedule: setPartialEditedSchedule,
      }),
      runsSubTabDefinition(),
      metricsSubTabDefinition(),
    ];
  }, [
    currentEditedSchedule,
    basicConfigSubTabDefinition,
    setPartialEditedSchedule,
    writeBackConfigSubTabDefinition,
    runsSubTabDefinition,
    metricsSubTabDefinition,
  ]);

  const subTabsPayload = useSubTabs({
    tabDefinitions: subTabDefinitions,
  });

  return (
    <AdminLayout currentPage={'DatabaseSchedule'}>
      <AdminBreadcrumbs
        items={[
          { label: 'Home', to: '/admin' },
          { label: 'Organizations', to: '/organizations' },
          {
            label: 'Databases',
            to: `/organizations/${organizationId}/databases`,
          },
          {
            label: 'Schedules',
            to: `/organizations/${organizationId}/databases/${databaseId}/schedules`,
          },
          {
            label: 'Edit Schedule',
            to: `/organizations/${organizationId}/databases/${databaseId}/schedule/${scheduleId}`,
          },
        ]}
      />
      {readWriteScheduleConfigPattern.status === 'loading' && (
        <S2Typography
          variant={'headline-4-alt'}
          translatedContent={'Loading...'}
        />
      )}
      {!currentEditedSchedule &&
        readWriteScheduleConfigPattern.status !== 'loading' && (
          <S2Typography
            variant={'headline-4-alt'}
            translatedContent={'Not configured'}
          />
        )}
      {syncDataWritePattern.status === 'error' && (
        <S2Typography
          variant={'headline-4-alt'}
          translatedContent={'Failed to start sync data.'}
          textColorVariant={'red-600'}
        />
      )}
      {readWriteScheduleConfigPattern.status === 'error' && (
        <S2Typography
          variant={'headline-4-alt'}
          translatedContent={'Failed to load or save configuration.'}
          textColorVariant={'red-600'}
        />
      )}

      {readWriteScheduleConfigPattern.status === 'done' &&
        currentEditedSchedule && (
          <Spacing size={'lg'} direction={'vertical'}>
            <Spacing size={'md'} direction={'vertical'}>
              <TextActiveEditComponent
                text={currentEditedSchedule.name}
                setText={(newValue) => {
                  setPartialEditedSchedule({
                    name: newValue,
                  });
                }}
                label={'Name'}
              />
            </Spacing>

            <Spacing size={'sm'} direction={'vertical'}>
              <Spacing size={'lg'} direction={'horizontal'}>
                <S2Typography
                  variant={'headline-6-alt'}
                  translatedContent={language.nextScheduledTime()}
                />
                <S2Typography
                  variant={'headline-6-alt'}
                  translatedContent={language.dangerousUserInput({
                    value: currentEditedSchedule?.details?.nextScheduledEpoch
                      ? getDateTimeAsReadableString(
                          new Date(
                            currentEditedSchedule.details.nextScheduledEpoch,
                          ),
                        )
                      : 'N/A',
                  })}
                />
              </Spacing>

              <Spacing
                size={'sm'}
                direction={'horizontal'}
                className="items-center"
              >
                <S2Typography
                  variant={'headline-6-alt'}
                  translatedContent={language.interval()}
                />

                <select
                  value={currentEditedSchedule.scheduleConfigurationType}
                  aria-label={'configurationType'}
                  className="form-select w-fit"
                  onChange={(event) => {
                    setPartialEditedSchedule({
                      scheduleConfigurationType:
                        DatabaseScheduleConfigurationType.parse(
                          event.target.value,
                        ),
                    });
                  }}
                >
                  {DatabaseScheduleConfigurationTypes.map((option) => {
                    return (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    );
                  })}
                </select>
              </Spacing>

              <Spacing size={'lg'} direction={'horizontal'}>
                <S2Typography
                  variant={'headline-4-alt'}
                  translatedContent={language.status()}
                />

                <select
                  value={currentEditedSchedule.status}
                  aria-label={'statusType'}
                  className="form-select w-fit"
                  onChange={(event) => {
                    setPartialEditedSchedule({
                      status: event.target.value as DatabaseScheduleStatus,
                    });
                  }}
                >
                  {DatabaseScheduleStatuses.map((option) => {
                    return (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    );
                  })}
                </select>
              </Spacing>

              <Spacing size={'lg'} direction={'horizontal'}>
                <S2Typography
                  variant={'headline-6-alt'}
                  translatedContent="Amount of Retries Attempted"
                />
                <S2Typography
                  variant={'headline-6-alt'}
                  translatedContent={language.dangerousUserInput({
                    value: String(
                      currentEditedSchedule?.details
                        ?.amountOfRetriesAttempted ?? 0,
                    ),
                  })}
                />
              </Spacing>
            </Spacing>

            <SubTabs {...subTabsPayload} />

            <Spacing size={'lg'} direction={'horizontal'}>
              <Button
                behavior={{
                  type: 'onClick',
                  onClick: () => {
                    if (
                      readWriteScheduleConfigPattern.canWrite &&
                      currentEditedSchedule
                    ) {
                      setCurrentEditedSchedule(null);
                      readWriteScheduleConfigPattern.saveEntity(
                        currentEditedSchedule,
                        () => {
                          if (
                            readWriteScheduleConfigPattern.status === 'done'
                          ) {
                            readWriteScheduleConfigPattern.reloadEntity();
                          }
                        },
                      );
                    }
                  },
                }}
                variant={'primary'}
              >
                <S2Typography
                  variant={'headline-5-alt'}
                  translatedContent={'Save'}
                  textColorVariant={'white'}
                />
              </Button>

              <Button
                behavior={{
                  type: 'onClick',
                  onClick: () => {
                    if (
                      readWriteScheduleConfigPattern.canWrite &&
                      currentEditedSchedule
                    ) {
                      setCurrentEditedSchedule(null);
                      readWriteScheduleConfigPattern.saveEntity(
                        currentEditedSchedule,
                        () => {
                          if (
                            readWriteScheduleConfigPattern.status === 'done'
                          ) {
                            readWriteScheduleConfigPattern.reloadEntity();
                          }
                        },
                      );
                    }
                  },
                }}
                variant={'primary'}
              >
                <S2Typography
                  variant={'headline-5-alt'}
                  translatedContent={'De-Configure'}
                  textColorVariant={'white'}
                />
              </Button>
            </Spacing>
          </Spacing>
        )}
    </AdminLayout>
  );
};
